import { lazy } from 'react';

// React.lazy wrapper that survives deploys.
//
// When a new build ships while a tab is still running the previous build, the
// old code-split chunk filenames (e.g. /static/js/4435.<hash>.chunk.js) no
// longer exist on the server. Navigating to a not-yet-loaded route then throws
// "Loading chunk … failed". The cure is simply to reload: the network-first
// service worker fetches the fresh index.html, which points at the new bundle.
//
// We force exactly one reload, guarded by a sessionStorage flag so a genuinely
// unreachable chunk (offline, real 404) doesn't cause an infinite reload loop.
const RELOAD_KEY = 'zb-chunk-reloaded';

export function isChunkLoadError(err) {
  const message = err?.message || '';
  return (
    err?.name === 'ChunkLoadError' ||
    /Loading chunk [\w]+ failed|Failed to fetch dynamically imported module|error loading dynamically imported module/i.test(message)
  );
}

const storage = () => {
  try { return window.sessionStorage; } catch { return null; }
};

// When a dynamic import fails because its chunk no longer exists on the server
// (a deploy shipped while this tab still runs the old build), reload exactly
// once so the network-first service worker serves the fresh index.html, which
// points at the new chunk names. Guarded by a sessionStorage flag so a genuine
// 404 / offline chunk cannot loop. Returns true when it started a reload (the
// caller should then stop, since the page is about to navigate away). Used both
// for route chunks (below) and for the lazy locale chunks (lib/i18n.js), so a
// language switch right after a deploy self-heals instead of silently falling
// back to Spanish.
export function reloadOnceForStaleChunk(err) {
  const s = storage();
  if (isChunkLoadError(err) && s && !s.getItem(RELOAD_KEY)) {
    s.setItem(RELOAD_KEY, '1');
    try { window.location.reload(); } catch { /* non-browser */ }
    return true;
  }
  return false;
}

// Returns a lazy component with a `preload()` method.
//
// Once its code has been loaded (by preload() or by a first render), the
// component resolves synchronously: React draws it straight away instead of
// showing the Suspense spinner for one frame. index.js relies on this for the
// public pages that arrive prerendered — it preloads the page, then mounts,
// so the prerendered HTML is swapped for the identical live page with no
// spinner in between.
export default function lazyWithReload(factory) {
  let loaded = null;
  let pending = null;

  const load = () => {
    if (loaded) return Promise.resolve(loaded);
    if (!pending) {
      pending = factory().then(
        (mod) => {
          loaded = mod;
          // Success — clear the guard so a future deploy can reload again.
          storage()?.removeItem(RELOAD_KEY);
          return mod;
        },
        (err) => {
          pending = null;
          throw err;
        },
      );
    }
    return pending;
  };

  const Component = lazy(() => {
    // A thenable that answers at once: React then renders without suspending.
    if (loaded) return { then: (resolve) => resolve(loaded) };
    return load().catch((err) => {
      // Keep the Suspense fallback on screen until the reload takes over.
      if (reloadOnceForStaleChunk(err)) return new Promise(() => {});
      throw err;
    });
  });
  Component.preload = load;
  return Component;
}
